import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Notification, NotificationConfig, NotificationStyle, NotificationUpdate } from '../notification/notification.interface';
import { NotificationHandle } from '../notification/notification-handle';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  // Legacy event emitters - kept for backward compatibility
  public toastState: EventEmitter<[string, string]> = new EventEmitter<[string, string]>();
  public backgroundSpinnerState: EventEmitter<boolean> = new EventEmitter<boolean>();

  // New notification system
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$: Observable<Notification[]> = this.notificationsSubject.asObservable();

  // Notification history system
  private readonly MAX_HISTORY_SIZE = 20;
  // Default timeout for auto-closing notifications (in milliseconds)
  private readonly DEFAULT_TIMEOUT = 3500;
  private notificationHistorySubject = new BehaviorSubject<Notification[]>([]);
  public notificationHistory$: Observable<Notification[]> = this.notificationHistorySubject.asObservable();

  // Notification container visibility
  private containerVisibleSubject = new BehaviorSubject<boolean>(true);
  public containerVisible$: Observable<boolean> = this.containerVisibleSubject.asObservable();

  // Legacy properties
  private toastMessage = "";
  private isBackgroundLoading = false;
  private progress = -1;
  private progressMessage = "";

  // Store the current progress notification handle for legacy SetProgress compatibility
  private currentProgressHandle: NotificationHandle | null = null;

  // Legacy getters
  public get Progress(): number {
    return this.progress;
  }

  public get ProgressMessage(): string {
    return this.progressMessage;
  }

  // Legacy IsBackgroundLoading property
  public get IsBackgroundLoading(): boolean {
    return this.isBackgroundLoading;
  }

  public set IsBackgroundLoading(value: boolean) {
    this.isBackgroundLoading = value;
    this.backgroundSpinnerState.emit(value);
  }

  constructor() { }

  /**
   * Create a new notification and return a handle to control it
   * @param message - Message text to display
   * @param config - Optional configuration
   * @returns NotificationHandle for controlling the notification
   */
  public createNotification(message: string, config?: NotificationConfig): NotificationHandle {
    const id = this.generateId();
    const style = config?.style || 'primary';

    // Error messages (danger) never timeout by default unless explicitly set
    const defaultTimeout = style === 'danger' ? -1 : this.DEFAULT_TIMEOUT;

    const notification: Notification = {
      id,
      message,
      style,
      showCloseButton: config?.showCloseButton !== undefined ? config.showCloseButton : true,
      timeout: config?.timeout !== undefined ? config.timeout : defaultTimeout,
      progress: config?.progress !== undefined ? config.progress : -1,
      icon: this.getIconForStyle(style),
      timestamp: Date.now(),
      actionButton: config?.actionButton
    };

    // Add to the beginning of the array (newest first)
    const currentNotifications = this.notificationsSubject.value;
    this.notificationsSubject.next([notification, ...currentNotifications]);

    // Add to history immediately upon creation
    this.addToHistory(notification);

    // Create and return handle
    return new NotificationHandle(
      id,
      (id, updates) => this.updateNotificationById(id, updates),
      (id) => this.closeNotificationById(id)
    );
  }

  /**
   * Update an existing notification
   * @param handle - Notification handle
   * @param updates - Updates to apply
   */
  public updateNotification(handle: NotificationHandle, updates: NotificationUpdate): void {
    this.updateNotificationById(handle.id, updates);
  }

  /**
   * Close a notification by its handle
   * @param handle - Notification handle
   */
  public closeNotification(handle: NotificationHandle): void {
    this.closeNotificationById(handle.id);
  }

  /**
   * Update notification by ID (called by NotificationHandle)
   */
  private updateNotificationById(id: string, updates: NotificationUpdate): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notification => {
      if (notification.id === id) {
        const updatedNotification = { ...notification };

        if (updates.message !== undefined) {
          updatedNotification.message = updates.message;
        }
        if (updates.progress !== undefined) {
          updatedNotification.progress = updates.progress;
        }
        if (updates.style !== undefined) {
          updatedNotification.style = updates.style;
          updatedNotification.icon = this.getIconForStyle(updates.style);
        }
        // Use 'in' operator to check if actionButton key exists (allows setting to undefined to remove button)
        if ('actionButton' in updates) {
          updatedNotification.actionButton = updates.actionButton;
        }
        // Handle autoClose: set default timeout to enable auto-close
        if ('autoClose' in updates && updates.autoClose) {
          updatedNotification.timeout = this.DEFAULT_TIMEOUT;
        }
        // Handle showCloseButton update
        if (updates.showCloseButton !== undefined) {
          updatedNotification.showCloseButton = updates.showCloseButton;
        }

        return updatedNotification;
      }
      return notification;
    });

    this.notificationsSubject.next(updatedNotifications);
  }

  /**
   * Close notification by ID (called by NotificationHandle or component)
   * Made public so the container component can call it directly
   */
  public closeNotificationById(id: string): void {
    const currentNotifications = this.notificationsSubject.value;
    const filteredNotifications = currentNotifications.filter(n => n.id !== id);
    this.notificationsSubject.next(filteredNotifications);
  }

  /**
   * Add notification to history (private method)
   * Maintains a maximum of MAX_HISTORY_SIZE notifications
   */
  private addToHistory(notification: Notification): void {
    const currentHistory = this.notificationHistorySubject.value;
    const newHistory = [notification, ...currentHistory];

    // Keep only last MAX_HISTORY_SIZE notifications
    if (newHistory.length > this.MAX_HISTORY_SIZE) {
      newHistory.splice(this.MAX_HISTORY_SIZE);
    }

    this.notificationHistorySubject.next(newHistory);
  }

  /**
   * Get icon class based on notification style
   */
  private getIconForStyle(style: NotificationStyle): string {
    switch (style) {
      case 'success':
        return 'bi-check-circle-fill';
      case 'danger':
        return 'bi-x-circle-fill';
      case 'warning':
        return 'bi-exclamation-triangle-fill';
      case 'primary':
      case 'secondary':
      default:
        return 'bi-info-circle-fill';
    }
  }

  /**
   * Generate unique ID for notifications
   */
  private generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Toggle notification container visibility
   */
  public toggleContainerVisibility(): void {
    this.containerVisibleSubject.next(!this.containerVisibleSubject.value);
  }

  /**
   * Get current container visibility state
   */
  public get isContainerVisible(): boolean {
    return this.containerVisibleSubject.value;
  }

  /**
   * Get notification history
   */
  public get notificationHistory(): Notification[] {
    return this.notificationHistorySubject.value;
  }

  // ========== Legacy Methods (Backward Compatibility) ==========

  /**
   * Legacy method: Show a toast notification
   * Creates a notification with 3.5s timeout and close button (except danger style which has no timeout)
   */
  public ShowToast(message: string, style: 'success' | 'warning' | 'danger' | 'primary' | 'secondary' = 'primary'): void {
    this.toastMessage = message;
    this.toastState.emit([message, style]);

    // Error messages (danger) never timeout by default
    const timeout = style === 'danger' ? -1 : this.DEFAULT_TIMEOUT;

    // Also create notification in new system
    this.createNotification(message, {
      style,
      timeout,
      showCloseButton: true,
      progress: -1
    });
  }

  /**
   * Legacy method: Hide toast (not used in new system, kept for compatibility)
   */
  public HideToast(): void {
    this.toastMessage = "";
    this.toastState.emit(["", ""]);
  }

  /**
   * Legacy method: Set progress bar
   * Creates or updates a progress notification
   * @param progress - Progress value (0-100 shows progress, -1 closes notification)
   * @param message - Progress message
   */
  public SetProgress(progress: number, message = ""): void {
    this.progress = progress;
    this.progressMessage = message;

    if (progress === -1) {
      // Close the progress notification
      if (this.currentProgressHandle) {
        this.currentProgressHandle.close();
        this.currentProgressHandle = null;
      }
    } else {
      // Create or update progress notification
      if (!this.currentProgressHandle) {
        // Create new progress notification
        this.currentProgressHandle = this.createNotification(message, {
          style: 'warning',
          timeout: -1, // No timeout for progress
          showCloseButton: true, // Allow manual closing
          progress: progress
        });
      } else {
        // Update existing progress notification
        this.currentProgressHandle.update({
          message: message,
          progress: progress
        });
      }
    }
  }
}
